import {useQuery} from "@tanstack/react-query";
import {useCallback, useRef} from "react";
import {
    GetTransactionsPaginatedResponse,
    GetTransactionsPaginatedSort,
    SortOrder,
    Token,
    TransactionsApi,
} from "@devour/client";
import getConfig from "@/utils/getConfig";
import {useStatsigClient} from "@statsig/react-bindings";

interface TransactionsPaginatedQueryParams {
    sort?: GetTransactionsPaginatedSort;
    order?: SortOrder;
    limit?: number;
    offset?: number
}

async function fetchTransactionsPaginated(fullToken: Token, userId: string, params: TransactionsPaginatedQueryParams): Promise<GetTransactionsPaginatedResponse> {
    return await new TransactionsApi(getConfig(fullToken)).getTransactionsPaginatedId({
        ...params,
        id: userId,
    });
}

function useTransactionsPaginatedQuery(fullToken: Token, userId: string, params: TransactionsPaginatedQueryParams, disabled?: boolean) {
    const { client: statsigClient } = useStatsigClient();
    const fuelStatus = !statsigClient.checkGate(import.meta.env.VITE_TOKEN_STATSIG_STATUS);

    return {
        queryKey: [
            "transactions-paginated",
            fullToken?.id ?? "unauthenticated",
            userId ?? "no-users-id",
            params,
        ],
        queryFn: () => fetchTransactionsPaginated(fullToken, userId, params),
        enabled: Boolean(fullToken && userId && fuelStatus) && !disabled,
        keepPreviousData: true,
        staleTime: 30 * 1000, // Consider data fresh for 30 seconds
        gcTime: 2 * 60 * 1000, // Keep in cache for 2 minutes
        refetchOnWindowFocus: false, // Don't refetch on tab switch - we have cache
    } as const;
}

export function useGetTransactionsPaginated(fullToken: Token, userId: string, params?: TransactionsPaginatedQueryParams, disabled?: boolean) {
    const query = useQuery(useTransactionsPaginatedQuery(fullToken, userId, params, disabled));

    /*
     * React Query's refetch() BYPASSES `enabled`, so an imperative refetch() with an
     * unresolved userId would call getTransactionsPaginatedId with id=undefined — the
     * generated client throws RequiredError, shown to the user as an error modal.
     * Guard refetch at the source so every consumer is inherently safe.
     */
    const latest = useRef(query);
    latest.current = query;
    const safeRefetch = useCallback<typeof query.refetch>(
        (options) => fullToken && userId ? latest.current.refetch(options) : Promise.resolve(latest.current),
        [fullToken, userId],
    );

    return {...query, refetch: safeRefetch};
}